import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { SUPPORT } from "../config/constants";

const SUPPORT_KEY = ["support-tickets"];

const invalidateSupport = (queryClient, ticketId) => {
  queryClient.invalidateQueries({ queryKey: SUPPORT_KEY });
  queryClient.invalidateQueries({ queryKey: ["support-unread"] });
  if (ticketId) {
    queryClient.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
    queryClient.invalidateQueries({ queryKey: ["support-messages", ticketId] });
  }
};

export const useGetAllSupportTickets = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...SUPPORT_KEY, params],
    queryFn: async ({ signal }) => {
      const response = await Get(SUPPORT.getAllTickets, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetSupportUnreadCount = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["support-unread"],
    queryFn: async () => {
      const response = await Get(SUPPORT.getUnreadCount, token);
      return response?.data?.count || 0;
    },
    enabled: !!token,
  });
};

export const useGetSupportTicketById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["support-ticket", id],
    queryFn: async () => {
      const response = await Get(SUPPORT.getTicketById + id, token);
      return response?.data?.ticket;
    },
    enabled: !!token && !!id,
  });
};

export const useGetSupportMessages = (ticketId, params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["support-messages", ticketId, params],
    queryFn: async ({ signal }) => {
      const response = await Get(SUPPORT.getMessages + ticketId, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token && !!ticketId,
    placeholderData: keepPreviousData,
  });
};

export const useCreateSupportTicket = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (data) => {
      const response = await Post(SUPPORT.createTicket, data, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to create ticket");
    },
    onSuccess: () => invalidateSupport(queryClient),
  });
};

export const useSendSupportMessage = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ ticketId, body }) => {
      const response = await Post(SUPPORT.sendMessage + ticketId, { body }, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to send message");
    },
    onSuccess: (_data, variables) => invalidateSupport(queryClient, variables.ticketId),
  });
};

export const useUpdateSupportTicket = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await Post(SUPPORT.updateTicket + id, data, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update ticket");
    },
    onSuccess: (_data, variables) => invalidateSupport(queryClient, variables.id),
  });
};

export const useMarkSupportTicketRead = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (ticketId) => {
      const response = await Post(SUPPORT.markRead + ticketId, {}, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to mark ticket read");
    },
    onSuccess: (_data, ticketId) => invalidateSupport(queryClient, ticketId),
  });
};
